/**
 * @Author: v_jppjpeng
 * @Date: 2023-08-03 16:32:11
 * @Explain: 气泡 - 文字提示
 */
import classNames from 'classnames';
import { useMemo } from 'react';
import { Tooltip as TdTooltip, TooltipProps } from 'tdesign-react';

type PropsType = {
  children?: React.ReactNode;
  /** 浮层类名 */
  overlayClassName?: TooltipProps['overlayClassName'];
  /** 浮层样式 */
  overlayStyle?: TooltipProps['overlayStyle'];
  /** 浮层内容类名 */
  overlayInnerClassName?: TooltipProps['overlayInnerClassName'];
  /** 浮层内容部分样式 */
  overlayInnerStyle?: TooltipProps['overlayInnerStyle'];
  /**
   * 用于设置提示默认显示多长时间之后消失，初始第一次有效，单位：毫秒
   */
  duration?: number;
  /** 是否展示三角 */
  showArrow?: boolean;
  /**
   * 是否在关闭浮层时销毁浮层
   * @default true
   */
  destroyOnClose?: boolean;
  theme?: TooltipProps['theme'];
  content: TooltipProps['content'];
  placement?: TooltipProps['placement'];
  placementOffset?: any[];
  /** 容器最小宽度 */
  minWidth?: any;
  /** 容器最大宽度 */
  maxWidth?: any;
  /** 气泡提示 - 所有参数 */
  tooltipProps?: TooltipProps;
};

function Tooltip(props: PropsType) {
  const {
    overlayClassName,
    overlayStyle,
    overlayInnerClassName,
    overlayInnerStyle,
    children,
    duration,
    showArrow = true,
    destroyOnClose = true,
    theme,
    content,
    placement = 'top',
    placementOffset,
    maxWidth,
    minWidth,
    tooltipProps,
  } = props;

  const { hideEmptyPopup = false } = tooltipProps || {};

  const placementOffsetStyle = useMemo(() => {
    const [num1, num2] = placementOffset || [];
    const topArr = ['top', 'top-left', 'top-right'];
    const bottomArr = ['bottom', 'bottom-left', 'bottom-right'];
    const leftArr = ['left', 'left-top', 'left-bottom'];
    const rightArr = ['right', 'right-top', 'right-bottom'];
    if (placement) {
      const isTop = topArr?.includes(placement);
      const isBottom = bottomArr?.includes(placement);
      const isLeft = leftArr?.includes(placement);
      const isRight = rightArr?.includes(placement);
      if (isTop) {
        return {
          marginBottom: num1,
          marginLeft: num2,
        };
      } else if (isBottom) {
        return {
          marginTop: num1,
          marginLeft: num2,
        };
      } else if (isLeft) {
        return {
          marginTop: num1,
          marginRight: num2,
        };
      } else if (isRight) {
        return {
          marginTop: num1,
          marginLeft: num2,
        };
      }
    }
    return {};
  }, [placement, placementOffset]);

  const popupProps = useMemo(() => {
    return {
      overlayClassName: classNames('default-td-tooltip', overlayClassName),
      overlayStyle: {
        maxWidth: maxWidth,
        minWidth: minWidth,
        ...placementOffsetStyle,
        ...overlayStyle,
      },
      overlayInnerClassName,
      overlayInnerStyle,
      duration,
      showArrow,
      destroyOnClose,
      theme,
      /**  配合hideEmptyPopup Props */
      content: (!hideEmptyPopup || content) && (
        <div className="custom-content">{content}</div>
      ),
      placement,
    };
  }, [
    overlayClassName,
    overlayStyle,
    overlayInnerClassName,
    overlayInnerStyle,
    placementOffsetStyle,
    showArrow,
    destroyOnClose,
    content,
    placement,
    duration,
    hideEmptyPopup,
  ]);

  return (
    <TdTooltip {...popupProps} {...tooltipProps}>
      {children}
    </TdTooltip>
  );
}

export default Tooltip;
